import { useEffect, useId, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { RiAddLine, RiArrowDownSLine, RiDeleteBinLine, RiLockLine } from '@remixicon/react'
import {
  effectiveMaxRows,
  TABLE_LIMITS,
  tableErrors,
  type AnswerValue,
  type SurveyQuestion,
  type TableCellValue,
  type TableColumn,
} from 'shared'
import { Button } from '../ui'
import { cx, focusRing } from '../../lib/utils'
import { blankRow, cellText, initialRows, rowState, type RowState } from './tableAnswers'

interface TableFieldProps {
  question: SurveyQuestion
  value: AnswerValue
  onChange: (value: AnswerValue) => void
  /** Cells cannot be edited and show their text: a submitted answer, or an operator reading it. */
  readOnly?: boolean
  disabled?: boolean
  /** Mark every cell the rules refuse. Off until the person has tried to submit. */
  showErrors?: boolean
  /**
   * `author` is the operator writing the rows the company will start from: no
   * fixed cells, nothing required, and every row can be removed.
   */
  mode?: 'answer' | 'author'
}

const cell =
  'w-full bg-transparent px-3 py-2.5 text-sm text-gray-900 outline-none dark:text-gray-50 ' +
  'focus:bg-primary-50 dark:focus:bg-primary-400/10 disabled:cursor-not-allowed disabled:opacity-60'
const th =
  'border border-gray-200 bg-gray-50 px-3 py-2 text-left text-xs font-semibold text-gray-700 ' +
  'dark:border-gray-800 dark:bg-gray-900 dark:text-gray-300'
const td = 'border border-gray-200 p-0 align-top dark:border-gray-800'
const tdFixed = cx(
  td,
  'bg-gray-100 px-3 py-2.5 text-sm text-gray-900 dark:bg-gray-900/60 dark:text-gray-100',
)

/**
 * A table the person fills in: rows the author wrote (which stay), rows they add,
 * and a cell per column typed by what the column holds.
 *
 * It only ever edits an array of rows and hands the whole array back, which is
 * also what the API stores — so what is on screen is what is saved.
 */
export default function TableField({
  question,
  value,
  onChange,
  readOnly,
  disabled,
  showErrors,
  mode = 'answer',
}: TableFieldProps) {
  const { t } = useTranslation()
  const config = question.config ?? {}
  const columns = config.columns ?? []
  const author = mode === 'author'
  const seedIds = useMemo(
    () => new Set((author ? [] : (config.seedRows ?? [])).map((seed) => seed.row_id)),
    [author, config.seedRows],
  )
  const rows = initialRows(question, value)
  const maxRows = effectiveMaxRows(config)
  const canAdd = !readOnly && !disabled && (author || config.allowNewRows !== false) && rows.length < maxRows
  const fieldId = useId()

  const cellErrors = useMemo(() => {
    const map = new Map<string, string>()
    if (!showErrors || readOnly) return map
    // The author is writing the rows, so none of them is "the author's row" yet.
    const checked: SurveyQuestion = author
      ? { ...question, config: { ...config, seedRows: [], allowNewRows: true } }
      : question
    for (const error of tableErrors(checked, rows, author ? 'shape' : 'complete')) {
      const key = `${error.cell.row_id}:${error.cell.column}`
      if (!map.has(key)) map.set(key, t(`survey.errors.${error.code}`, { ...error.params }))
    }
    return map
    // `rows` is derived from `value`, so tracking the value is enough.
  }, [question, value, showErrors, readOnly, author, t]) // eslint-disable-line react-hooks/exhaustive-deps

  const setCell = (rowId: string, key: string, next: TableCellValue) =>
    onChange(
      rows.map((row) =>
        row.row_id === rowId ? { ...row, values: { ...row.values, [key]: next } } : row,
      ),
    )

  const addRow = () => onChange([...rows, blankRow(rows, author ? 'A' : 'N')])
  const removeRow = (rowId: string) => onChange(rows.filter((row) => row.row_id !== rowId))

  if (columns.length === 0) {
    return <p className="text-sm text-gray-500 dark:text-gray-400">{t('survey.table.noColumns')}</p>
  }

  const states = new Map<string, RowState>(rows.map((row) => [row.row_id, rowState(question, row)]))
  const done = rows.filter((row) => states.get(row.row_id) === 'ok').length
  const showStatus = !readOnly && !author
  const showCode = author

  return (
    <div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[640px] border-collapse" aria-labelledby={`q-${question.id}-label`}>
          <thead>
            <tr>
              {showStatus && <th className={cx(th, 'w-9')}><span className="sr-only">{t('survey.table.status')}</span></th>}
              {showCode && <th className={cx(th, 'w-20')}>{t('survey.table.code')}</th>}
              {columns.map((column) => (
                <th
                  key={column.key}
                  className={cx(th, column.type === 'single_choice' ? 'min-w-[165px]' : 'min-w-[130px]')}
                  scope="col"
                >
                  {column.label}
                  {column.required && !author && (
                    <span className="ml-0.5 text-red-500" aria-hidden="true">*</span>
                  )}
                  {column.type === 'multi_choice' && column.maxSelect && (
                    <span className="block text-[11px] font-normal text-gray-500 dark:text-gray-400">
                      {t('survey.table.upTo', { count: column.maxSelect })}
                    </span>
                  )}
                  {column.unit && (
                    <span className="block text-[11px] font-normal text-gray-500 dark:text-gray-400">
                      {column.unit}
                    </span>
                  )}
                </th>
              ))}
              {!readOnly && <th className={cx(th, 'w-10')}><span className="sr-only">{t('common.delete')}</span></th>}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={columns.length + 3} className={cx(td, 'px-3 py-4 text-sm text-gray-500 dark:text-gray-400')}>
                  {t('survey.table.empty')}
                </td>
              </tr>
            )}
            {rows.map((row) => {
              const fixedRow = seedIds.has(row.row_id)
              return (
                <tr key={row.row_id} data-row-id={row.row_id}>
                  {showStatus && (
                    <td className={cx(td, 'text-center align-middle')}>
                      <StatusDot state={states.get(row.row_id) ?? 'empty'} />
                    </td>
                  )}
                  {showCode && (
                    <td className={cx(tdFixed, 'font-mono text-xs text-gray-500')}>{row.row_id}</td>
                  )}
                  {columns.map((column) => {
                    const locked = fixedRow && column.lockedOnSeed === true
                    const current = row.values[column.key]
                    const error = cellErrors.get(`${row.row_id}:${column.key}`)
                    const label = `${column.label} · ${row.row_id}`

                    if (locked || readOnly) {
                      return (
                        <td key={column.key} className={tdFixed} data-locked={locked || undefined}>
                          <span className="flex items-start gap-1.5">
                            {locked && !readOnly && (
                              <RiLockLine className="mt-0.5 size-3.5 shrink-0 text-gray-400" aria-hidden="true" />
                            )}
                            {cellText(column, current) || (readOnly ? '—' : '')}
                          </span>
                        </td>
                      )
                    }

                    return (
                      <td
                        key={column.key}
                        className={cx(td, error && 'bg-red-50 ring-1 ring-inset ring-red-400 dark:bg-red-500/10')}
                        title={error}
                      >
                        <CellControl
                          column={column}
                          label={label}
                          value={current}
                          disabled={disabled}
                          invalid={Boolean(error)}
                          onChange={(next) => setCell(row.row_id, column.key, next)}
                        />
                        {error && <span className="sr-only">{error}</span>}
                      </td>
                    )
                  })}
                  {!readOnly && (
                    <td className={cx(td, 'text-center align-middle')}>
                      {!fixedRow && (
                        <button
                          type="button"
                          aria-label={t('survey.table.removeRow', { row: row.row_id })}
                          disabled={disabled}
                          onClick={() => removeRow(row.row_id)}
                          className={cx(
                            'rounded-md p-1.5 text-gray-400 transition hover:bg-red-50 hover:text-red-600 disabled:opacity-30 dark:hover:bg-red-500/10',
                            focusRing,
                          )}
                        >
                          <RiDeleteBinLine className="size-4" aria-hidden="true" />
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {!readOnly && (
        <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            {(author || config.allowNewRows !== false) && (
              <Button variant="secondary" onClick={addRow} disabled={!canAdd} aria-describedby={`${fieldId}-left`}>
                <RiAddLine className="-ml-1 mr-1.5 size-4" aria-hidden="true" />
                {t(author ? 'survey.table.addSeedRow' : 'survey.table.addRow')}
              </Button>
            )}
            <span id={`${fieldId}-left`} className="text-sm text-gray-500 dark:text-gray-400">
              {rows.length >= maxRows
                ? t('survey.table.limitReached', { count: maxRows })
                : t('survey.table.rowsLeft', { count: maxRows - rows.length })}
            </span>
          </div>
          {showStatus && (
            <div className="flex flex-wrap items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
              <span aria-live="polite">{t('survey.table.complete', { done, total: rows.length })}</span>
              <span className="flex items-center gap-1.5"><StatusDot state="ok" /> {t('survey.table.legendOk')}</span>
              <span className="flex items-center gap-1.5"><StatusDot state="part" /> {t('survey.table.legendPart')}</span>
              <span className="flex items-center gap-1.5"><StatusDot state="empty" /> {t('survey.table.legendEmpty')}</span>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function StatusDot({ state }: { state: RowState }) {
  const { t } = useTranslation()
  return (
    <span
      role="img"
      aria-label={t(`survey.table.state_${state}`)}
      title={t(`survey.table.state_${state}`)}
      data-state={state}
      className={cx(
        'inline-block size-2.5 rounded-full',
        state === 'ok' ? 'bg-emerald-500' : state === 'part' ? 'bg-amber-500' : 'bg-gray-300 dark:bg-gray-700',
      )}
    />
  )
}

function CellControl({
  column,
  label,
  value,
  disabled,
  invalid,
  onChange,
}: {
  column: TableColumn
  label: string
  value: TableCellValue | undefined
  disabled?: boolean
  invalid: boolean
  onChange: (value: TableCellValue) => void
}) {
  const { t } = useTranslation()

  switch (column.type) {
    case 'number':
      return (
        <input
          type="number"
          aria-label={label}
          aria-invalid={invalid || undefined}
          disabled={disabled}
          min={column.min}
          max={column.max}
          value={typeof value === 'number' ? String(value) : ''}
          onChange={(event) => onChange(event.target.value === '' ? null : Number(event.target.value))}
          className={cell}
        />
      )

    case 'single_choice':
      return (
        <select
          aria-label={label}
          aria-invalid={invalid || undefined}
          disabled={disabled}
          value={typeof value === 'string' ? value : ''}
          onChange={(event) => onChange(event.target.value || null)}
          className={cx(cell, 'cursor-pointer')}
        >
          <option value="">—</option>
          {(column.options ?? []).map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      )

    case 'multi_choice':
      return (
        <MultiCell
          column={column}
          label={label}
          value={Array.isArray(value) ? (value as string[]) : []}
          disabled={disabled}
          onChange={(next) => onChange(next.length > 0 ? next : null)}
        />
      )

    case 'short_text':
    default:
      return (
        <input
          type="text"
          aria-label={label}
          aria-invalid={invalid || undefined}
          disabled={disabled}
          maxLength={Math.min(column.maxLength ?? TABLE_LIMITS.maxTextLength, TABLE_LIMITS.maxTextLength)}
          value={typeof value === 'string' ? value : ''}
          placeholder={t('survey.table.typeHere')}
          onChange={(event) => onChange(event.target.value)}
          className={cx(cell, 'placeholder:text-gray-400')}
        />
      )
  }
}

/** A choice list in a popover: it has to fit in a cell, and still hold the column's limit. */
function MultiCell({
  column,
  label,
  value,
  disabled,
  onChange,
}: {
  column: TableColumn
  label: string
  value: string[]
  disabled?: boolean
  onChange: (value: string[]) => void
}) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const root = useRef<HTMLDivElement>(null)
  const options = column.options ?? []
  const limit = column.maxSelect ?? options.length
  const labelOf = (entry: string) => options.find((option) => option.value === entry)?.label ?? entry

  useEffect(() => {
    if (!open) return
    const close = (event: MouseEvent | KeyboardEvent) => {
      if (event instanceof KeyboardEvent) {
        if (event.key === 'Escape') setOpen(false)
      } else if (root.current && !root.current.contains(event.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', close)
    document.addEventListener('keydown', close)
    return () => {
      document.removeEventListener('mousedown', close)
      document.removeEventListener('keydown', close)
    }
  }, [open])

  return (
    <div ref={root} className="relative">
      <button
        type="button"
        aria-label={label}
        aria-haspopup="listbox"
        aria-expanded={open}
        disabled={disabled}
        onClick={() => setOpen((current) => !current)}
        className={cx(
          'flex min-h-[42px] w-full items-center gap-1 px-3 py-2 text-left text-sm disabled:opacity-60',
          focusRing,
        )}
      >
        <span className="flex flex-1 flex-wrap gap-1">
          {value.length === 0 && (
            <span className="text-gray-400">
              {column.maxSelect ? t('survey.table.pickUpTo', { count: limit }) : t('survey.table.pick')}
            </span>
          )}
          {value.map((entry) => (
            <span
              key={entry}
              className="rounded bg-primary-50 px-1.5 py-0.5 text-xs font-medium text-primary-700 dark:bg-primary-400/10 dark:text-primary-400"
            >
              {labelOf(entry)}
            </span>
          ))}
        </span>
        {column.maxSelect && (
          <span className="font-mono text-xs text-gray-400">
            {value.length}/{limit}
          </span>
        )}
        <RiArrowDownSLine className="size-4 text-gray-400" aria-hidden="true" />
      </button>

      {open && (
        <div
          role="listbox"
          aria-label={label}
          aria-multiselectable="true"
          className="absolute left-0 top-full z-20 mt-1 max-h-64 w-64 overflow-y-auto rounded-lg border border-gray-200 bg-white p-1.5 shadow-lg dark:border-gray-800 dark:bg-gray-950"
        >
          {column.maxSelect && (
            <p className="px-2 pb-1 pt-0.5 text-xs text-gray-500 dark:text-gray-400">
              {value.length >= limit
                ? t('survey.table.limitPicked', { count: limit })
                : t('survey.table.upTo', { count: limit })}
            </p>
          )}
          {options.map((option) => {
            const checked = value.includes(option.value)
            const blocked = !checked && value.length >= limit
            return (
              <label
                key={option.value}
                className={cx(
                  'flex items-center gap-2 rounded px-2 py-1.5 text-sm text-gray-700 dark:text-gray-200',
                  blocked ? 'cursor-not-allowed opacity-40' : 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-900',
                )}
              >
                <input
                  type="checkbox"
                  disabled={blocked}
                  checked={checked}
                  onChange={(event) => {
                    if (!event.target.checked) {
                      onChange(value.filter((entry) => entry !== option.value))
                    } else if (value.length < limit) {
                      // A disabled box cannot be clicked, but the limit is not left to that.
                      onChange([...value, option.value])
                    }
                  }}
                  className="size-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 dark:border-gray-700 dark:bg-gray-900"
                />
                {option.label}
              </label>
            )
          })}
        </div>
      )}
    </div>
  )
}
